/**
 * supportChat.ts — BotableX's own support chat on this staff app.
 *
 * After login, the server (IdentityAuthService `GET /api/auth/support-chat/identity`, reached as
 * `/api/identity/...` through the gateway) returns the chat-widget loader settings with an identity
 * SIGNED for the signed-in user — the role a tenant's own server plays in the documented HMAC
 * integration. We then load the stock widget `loader.js` exactly as any tenant site would.
 *
 * Rules (operator decisions, 2026-09-29):
 *   - only after login — never on the login page;
 *   - identified or nothing: any non-200, malformed body, or error mounts NOTHING;
 *   - the loader has no teardown, so mount at most once per page load and reload on logout.
 *
 * Kept framework-free so it is testable without React. Duplicated in agent-console on purpose: each
 * app's image builds from its own folder.
 */
import type { AxiosInstance } from 'axios';

export type SupportChatSite = 'admin-portal' | 'agent-console';

export const SUPPORT_CHAT_IDENTITY_PATH = '/api/identity/support-chat/identity';
export const SUPPORT_CHAT_LOADER_ID = 'botablex-support-chat-loader';

export interface SupportChatIdentityClaim {
  type: string;
  value: string;
}

export interface SupportChatSettings {
  widgetOrigin: string;
  widgetId: string;
  tenantId: string;
  position: string;
  identity: {
    identities: SupportChatIdentityClaim[];
    userHash: string;
    displayName: string;
  };
}

const nonEmpty = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;

/** The widget host must be a bare https origin (http only for localhost dev). */
export function isSafeWidgetOrigin(origin: unknown): origin is string {
  if (!nonEmpty(origin)) return false;
  try {
    const u = new URL(origin);
    if (u.origin !== origin.replace(/\/+$/, '')) return false; // no path, query or fragment
    if (u.protocol === 'https:') return true;
    return u.protocol === 'http:' && (u.hostname === 'localhost' || u.hostname === '127.0.0.1');
  } catch {
    return false;
  }
}

/** Accept the server response only if every field the widget needs is present and well-formed. */
export function parseSupportChatSettings(data: unknown): SupportChatSettings | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  const identity = d.identity as Record<string, unknown> | undefined;
  if (!isSafeWidgetOrigin(d.widgetOrigin) || !nonEmpty(d.widgetId) || !nonEmpty(d.tenantId)) return null;
  if (!identity || !nonEmpty(identity.userHash) || !Array.isArray(identity.identities)) return null;
  const identities = (identity.identities as unknown[]).filter(
    (c): c is SupportChatIdentityClaim =>
      !!c && typeof c === 'object' && nonEmpty((c as SupportChatIdentityClaim).type) && nonEmpty((c as SupportChatIdentityClaim).value),
  );
  if (identities.length === 0) return null;
  return {
    widgetOrigin: (d.widgetOrigin as string).replace(/\/+$/, ''),
    widgetId: d.widgetId as string,
    tenantId: d.tenantId as string,
    position: nonEmpty(d.position) ? (d.position as string) : 'bottom-right',
    identity: {
      identities,
      userHash: identity.userHash as string,
      displayName: nonEmpty(identity.displayName) ? (identity.displayName as string) : '',
    },
  };
}

/** Ask the server for this user's signed settings. Never throws; null means "show nothing". */
export async function fetchSupportChatSettings(
  client: Pick<AxiosInstance, 'get'>,
  site: SupportChatSite,
  userTenantId: string,
): Promise<SupportChatSettings | null> {
  try {
    const res = await client.get(SUPPORT_CHAT_IDENTITY_PATH, {
      params: { site },
      // Identity is the user's own tenant, never a platformAdmin's selected working tenant.
      headers: { 'X-Tenant-Id': userTenantId },
    });
    return res.status === 200 ? parseSupportChatSettings(res.data) : null;
  } catch {
    return null;
  }
}

export function isSupportChatMounted(doc: Document = document): boolean {
  return doc.getElementById(SUPPORT_CHAT_LOADER_ID) !== null;
}

/** Load the stock widget loader with these settings. At most once per page load. */
export function mountSupportChat(settings: SupportChatSettings, doc: Document = document): boolean {
  if (isSupportChatMounted(doc)) return false;
  const win = doc.defaultView as (Window & { CSSWidgetSettings?: unknown }) | null;
  if (!win) return false;
  win.CSSWidgetSettings = {
    widgetId: settings.widgetId,
    tenantId: settings.tenantId,
    position: settings.position,
    identity: settings.identity,
  };
  const script = doc.createElement('script');
  script.id = SUPPORT_CHAT_LOADER_ID;
  script.async = true;
  script.src = `${settings.widgetOrigin}/loader.js`;
  doc.body.appendChild(script);
  return true;
}

/** Indirection so tests can observe the logout reload (jsdom's location is not spy-able). */
export const supportChatNavigation = {
  reloadToLogin: (): void => {
    window.location.assign('/login');
  },
};
